import type { QuestionPart, SuggestedAction } from './aiAnalysisTypes';

type PartialHighlightCandidate = Pick<
  SuggestedAction,
  | 'answerStatus'
  | 'answered'
  | 'answer'
  | 'partialAnswer'
  | 'highlightedQuestion'
  | 'questionParts'
  | 'answeredParts'
  | 'unansweredParts'
>;

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export function getQuestionPartHighlightPresence(questionParts?: QuestionPart[] | null): {
  answered: boolean;
  unanswered: boolean;
} {
  let answered = false;
  let unanswered = false;

  for (const part of questionParts || []) {
    if (!hasText(part?.text)) continue;
    if (part.status === 'answered') answered = true;
    if (part.status === 'unanswered') unanswered = true;
  }

  return { answered, unanswered };
}

export function getMarkdownHighlightPresence(highlightedQuestion?: string | null): {
  answered: boolean;
  unanswered: boolean;
} {
  if (!hasText(highlightedQuestion)) {
    return { answered: false, unanswered: false };
  }

  return {
    answered: /\*\*[^*]+\*\*/.test(highlightedQuestion),
    unanswered: /~~[^~]+~~/.test(highlightedQuestion),
  };
}

export function hasMixedQuestionPartHighlights(questionParts?: QuestionPart[] | null): boolean {
  const presence = getQuestionPartHighlightPresence(questionParts);
  return presence.answered && presence.unanswered;
}

export function hasMixedMarkdownHighlights(highlightedQuestion?: string | null): boolean {
  const presence = getMarkdownHighlightPresence(highlightedQuestion);
  return presence.answered && presence.unanswered;
}

export function hasValidPartialHighlightShape(action: PartialHighlightCandidate): boolean {
  return (
    hasMixedQuestionPartHighlights(action.questionParts) ||
    hasMixedMarkdownHighlights(action.highlightedQuestion)
  );
}

export function hasValidPartialAnswerShape(action: PartialHighlightCandidate): boolean {
  const questionPartPresence = getQuestionPartHighlightPresence(action.questionParts);
  const markdownPresence = getMarkdownHighlightPresence(action.highlightedQuestion);
  const hasAnsweredEvidence =
    questionPartPresence.answered ||
    markdownPresence.answered ||
    Boolean(action.answeredParts?.some((part) => hasText(part?.part) || hasText(part?.answer)));
  const hasUnansweredEvidence =
    questionPartPresence.unanswered ||
    markdownPresence.unanswered ||
    Boolean(action.unansweredParts?.some((part) => hasText(part)));

  return hasAnsweredEvidence && hasUnansweredEvidence && hasValidPartialHighlightShape(action);
}

export function shouldRenderPartialHighlights(
  action: PartialHighlightCandidate,
  opts: { isPartial: boolean; isComplete?: boolean },
): boolean {
  const isComplete =
    opts.isComplete || action.answerStatus === 'complete' || action.answered === true;
  return opts.isPartial && !isComplete && hasValidPartialHighlightShape(action);
}

export function shouldRenderCompleteHighlights(action: PartialHighlightCandidate): boolean {
  const questionPartPresence = getQuestionPartHighlightPresence(action.questionParts);
  const markdownPresence = getMarkdownHighlightPresence(action.highlightedQuestion);
  return (
    questionPartPresence.answered ||
    markdownPresence.answered ||
    Boolean(action.answeredParts?.some((part) => hasText(part?.part) || hasText(part?.answer))) ||
    hasText(action.answer)
  );
}

export function sanitizeActionAnswerState<T extends PartialHighlightCandidate>(
  action: T,
  opts: { trustAnswerStatus?: boolean } = {},
): T {
  const isComplete = action.answerStatus === 'complete' || action.answered === true;

  if (isComplete) {
    const questionPartPresence = getQuestionPartHighlightPresence(action.questionParts);
    const markdownPresence = getMarkdownHighlightPresence(action.highlightedQuestion);

    return {
      ...action,
      partialAnswer: undefined,
      highlightedQuestion: markdownPresence.answered ? action.highlightedQuestion : undefined,
      questionParts: questionPartPresence.answered ? action.questionParts : undefined,
      unansweredParts: undefined,
    };
  }

  if (action.answerStatus === 'partial') {
    if (opts.trustAnswerStatus || hasValidPartialAnswerShape(action)) {
      return action;
    }

    return {
      ...action,
      answerStatus: undefined,
      partialAnswer: undefined,
      highlightedQuestion: undefined,
      questionParts: undefined,
      unansweredParts: undefined,
    };
  }

  return {
    ...action,
    partialAnswer: undefined,
    highlightedQuestion: undefined,
    questionParts: undefined,
    unansweredParts: undefined,
  };
}
